import { snapdom } from '@zumer/snapdom';
import { toBlob } from 'html-to-image';
import html2canvas from 'html2canvas';
import { isInIframe } from 'utils/proxyUtils';
import { getNearestNode } from './utils/node';

declare global {
  interface Window {
    __ATARIM_SCREENSHOT_MODE__?: boolean;
  }
}

interface CaptureOptions {
  onCaptureStart?: () => void;
  onCaptureEnd?: () => void;
  quality?: number;
  fast?: boolean;
  preserveElements?: boolean;
  useHtml2Canvas?: boolean;
  useBrowser?: boolean;
  useChromeApi?: boolean;
  useSnapdom?: boolean;
  useFallback?: boolean;
  selectedElement?: HTMLElement | null;
  elementPath?: string | null;
  fullPage?: boolean; // New option for full page capture
  borderStyle?: {
    color: string;
    width: number;
    style: string;
  };
}

export class ScreenCapture {
  private selectedElementRef: HTMLElement | null = null;
  private isCapturing = false;
  private _originalBodyOverflow = '';
  private _uiHidden = false;
  private originalStates = new Map<
    Element,
    {
      opacity: string;
      pointerEvents: string;
      scrollTop: number;
      scrollLeft: number;
      value?: string;
      checked?: boolean;
      outline?: string;
      transition?: string;
      visibility?: string;
      display?: string;
    }
  >();

  constructor() {
    // Constructor is now empty since we removed worker initialization
  }

  /**
   * Sets screenshot mode flag and cleans up UI elements for clean capture
   */
  private enableScreenshotMode(): void {
    // Set the screenshot mode flag
    window.__ATARIM_SCREENSHOT_MODE__ = true;

    // Store original overflow styles
    this._originalBodyOverflow = document.body.style.overflow;

    // Hide scrollbars
    document.body.style.overflow = 'hidden';

    // Clear any existing element selector styles before disabling
    const elementsWithAtarimStyles = document.querySelectorAll(
      '[data-atarim-selected]'
    );
    elementsWithAtarimStyles.forEach((element) => {
      const el = element as HTMLElement;
      el.style.removeProperty('outline');
      el.style.removeProperty('cursor');
      el.removeAttribute('data-atarim-selected');
    });
  }

  /**
   * Disables screenshot mode flag and restores UI elements
   */
  private disableScreenshotMode(): void {
    // Remove the screenshot mode flag
    delete window.__ATARIM_SCREENSHOT_MODE__;

    // Restore original overflow styles
    document.body.style.overflow = this._originalBodyOverflow || '';
  }

  private async handleSpecialElements() {
    // Handle Canvas elements
    const canvasElements = document.getElementsByTagName('canvas');
    for (const canvas of Array.from(canvasElements)) {
      if (this.isElementVisible(canvas)) {
        try {
          const dataUrl = canvas.toDataURL('image/png', 1.0);
          const img = document.createElement('img');
          img.src = dataUrl;
          img.style.position = 'absolute';
          img.style.top = canvas.offsetTop + 'px';
          img.style.left = canvas.offsetLeft + 'px';
          img.style.width = canvas.offsetWidth + 'px';
          img.style.height = canvas.offsetHeight + 'px';
          canvas.parentElement?.insertBefore(img, canvas);
          this.saveElementState(canvas);
          canvas.style.opacity = '0';
        } catch (e) {
          console.warn('Failed to capture canvas:', e);
        }
      }
    }

    // Handle iframes
    const iframeElements = document.getElementsByTagName('iframe');
    for (const iframe of Array.from(iframeElements)) {
      if (this.isElementVisible(iframe) && !iframe.hasAttribute('srcdoc')) {
        try {
          const iframeWindow = iframe.contentWindow;
          const iframeDocument = iframeWindow?.document;
          if (iframeDocument && iframeDocument.documentElement) {
            const iframeContent = iframeDocument.documentElement.outerHTML;
            const placeholder = document.createElement('div');
            placeholder.innerHTML = iframeContent;
            placeholder.style.position = 'absolute';
            placeholder.style.top = iframe.offsetTop + 'px';
            placeholder.style.left = iframe.offsetLeft + 'px';
            placeholder.style.width = iframe.offsetWidth + 'px';
            placeholder.style.height = iframe.offsetHeight + 'px';
            placeholder.style.overflow = 'hidden';
            iframe.parentElement?.insertBefore(placeholder, iframe);
            this.saveElementState(iframe);
            iframe.style.opacity = '0';
          }
        } catch (e) {
          console.warn('Failed to capture iframe:', e);
        }
      }
    }

    // Handle video elements
    const videoElements = document.getElementsByTagName('video');
    for (const video of Array.from(videoElements)) {
      if (this.isElementVisible(video)) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d')?.drawImage(video, 0, 0);
          const img = document.createElement('img');
          img.src = canvas.toDataURL();
          img.style.position = 'absolute';
          img.style.top = video.offsetTop + 'px';
          img.style.left = video.offsetLeft + 'px';
          img.style.width = video.offsetWidth + 'px';
          img.style.height = video.offsetHeight + 'px';
          video.parentElement?.insertBefore(img, video);
          this.saveElementState(video);
          video.style.opacity = '0';
        } catch (e) {
          console.warn('Failed to capture video:', e);
        }
      }
    }
  }

  private async captureHtmlToImage(
    options: CaptureOptions = {}
  ): Promise<string> {
    try {
      this.isCapturing = true;
      const {
        selectedElement,
        fullPage = false,
        borderStyle = { color: 'red', width: 3, style: 'dashed' },
      } = options;

      // Save scroll positions
      document.querySelectorAll('*').forEach((element) => {
        if (element.scrollTop > 0 || element.scrollLeft > 0) {
          this.saveElementState(element);
        }
      });

      if (selectedElement) {
        this.saveElementState(selectedElement);
        selectedElement.style.setProperty(
          'outline',
          `${borderStyle.width}px ${borderStyle.style} ${borderStyle.color}`,
          'important'
        );
      }

      if (options.preserveElements) {
        await this.handleSpecialElements();
      }

      // Configure dimensions based on full page or viewport mode
      let captureConfig;

      if (fullPage) {
        // Full page configuration
        const documentWidth = Math.max(
          document.documentElement.scrollWidth,
          document.documentElement.offsetWidth,
          document.documentElement.clientWidth
        );
        const documentHeight = Math.max(
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
          document.documentElement.clientHeight
        );

        captureConfig = {
          width: documentWidth,
          height: documentHeight,
          style: {
            transform: 'none',
            transformOrigin: '0 0',
            width: `${documentWidth}px`,
            height: `${documentHeight}px`,
          },
        };
      } else {
        // Viewport configuration
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        captureConfig = {
          width: viewportWidth,
          height: viewportHeight,
          style: {
            transform: `translate(-${scrollX}px, -${scrollY}px)`,
            transformOrigin: '0 0',
            width: `${document.documentElement.scrollWidth}px`,
            height: `${document.documentElement.scrollHeight}px`,
          },
        };
      }

      const blob = await toBlob(document.documentElement, {
        quality: options.quality || 0.95,
        backgroundColor: '#fff',
        filter: (node: Node) => {
          if (!(node instanceof Element)) return true;
          if (node.id === 'root-atarim') return false;
          if (node.classList?.contains('no-screenshot')) return false;
          if (node.hasAttribute('data-html2canvas-ignore')) return false;
          return true;
        },
        skipFonts: true,
        pixelRatio: fullPage ? 1 : window.devicePixelRatio, // Use 1 for full page to manage file size
        ...captureConfig,
      });

      if (!blob) throw new Error('Failed to generate blob');

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } finally {
      this.isCapturing = false;
      this.restoreElementStates();
    }
  }

  private async captureSnapdom(options: CaptureOptions = {}): Promise<string> {
    try {
      this.isCapturing = true;
      const {
        selectedElement,
        fullPage = false,
        borderStyle = { color: 'red', width: 3, style: 'dashed' },
      } = options;

      if (selectedElement && borderStyle) {
        if (!this.originalStates.has(selectedElement)) {
          this.saveElementState(selectedElement);
        }
        selectedElement.style.setProperty(
          'outline',
          `${borderStyle.width}px ${borderStyle.style} ${borderStyle.color}`,
          'important'
        );
        selectedElement.style.transition = 'none';
      }

      if (options.preserveElements) {
        await this.handleSpecialElements();
      }

      // Add delay to ensure UI elements are hidden and styles are applied
      await new Promise((resolve) =>
        setTimeout(resolve, isInIframe() ? 1000 : 400)
      );

      const snapshot = await snapdom.capture(document.body, {
        quality: 0.5,
        fast: options.fast || false,
        embedFonts: true,
        format: 'png',
        type: 'png',
        backgroundColor: '#fff',
        exclude: [
          '#root-atarim',
          '[data-atarim]',
          '[data-html2canvas-ignore]',
          '.no-screenshot',
        ],
      });

      const png = await snapshot.toPng();

      // If fullPage is true, return the full screenshot without cropping
      if (fullPage) {
        return png.src;
      }

      // Create image element from the full page screenshot
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = png.src;
      });

      // Get document dimensions
      const documentWidth = document.documentElement.scrollWidth;
      const documentHeight = document.documentElement.scrollHeight;

      // Calculate scale factors between screenshot and document
      const scaleX = img.naturalWidth / documentWidth;
      const scaleY = img.naturalHeight / documentHeight;

      let cropX = 0;
      let cropY = 0;
      let cropWidth = window.innerWidth * scaleX;
      let cropHeight = window.innerHeight * scaleY;

      if (selectedElement) {
        // Get element's position relative to the document (not viewport)
        const rect = selectedElement.getBoundingClientRect();
        const elementDocumentX = rect.left + window.scrollX;
        const elementDocumentY = rect.top + window.scrollY;

        // Calculate element center in document coordinates
        const elementCenterX = elementDocumentX + rect.width / 2;
        const elementCenterY = elementDocumentY + rect.height / 2;

        // Convert to screenshot coordinates and center the crop
        cropX = elementCenterX * scaleX - cropWidth / 2;
        cropY = elementCenterY * scaleY - cropHeight / 2;

        // Ensure we don't crop beyond image boundaries
        cropX = Math.max(0, Math.min(cropX, img.naturalWidth - cropWidth));
        cropY = Math.max(0, Math.min(cropY, img.naturalHeight - cropHeight));
      } else {
        // Use current scroll position for viewport cropping
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        // Convert scroll position to screenshot coordinates
        cropX = scrollX * scaleX;
        cropY = scrollY * scaleY;

        // Ensure crop dimensions don't exceed image boundaries
        cropX = Math.max(0, Math.min(cropX, img.naturalWidth - cropWidth));
        cropY = Math.max(0, Math.min(cropY, img.naturalHeight - cropHeight));
        cropWidth = Math.min(cropWidth, img.naturalWidth - cropX);
        cropHeight = Math.min(cropHeight, img.naturalHeight - cropY);
      }

      // Crop the image
      return await this.cropImageToPng(
        img,
        Math.round(cropX),
        Math.round(cropY),
        Math.round(cropWidth),
        Math.round(cropHeight),
        window.innerWidth,
        window.innerHeight
      );
    } catch (error) {
      console.error('Snapdom screenshot capture failed:', error);
      throw error;
    } finally {
      // Restore states regardless of success/failure
      this.restoreElementStates();
      this.isCapturing = false;
    }
  }

  // Add this helper method to your ScreenCapture class
  private async cropImageToPng(
    image: HTMLImageElement,
    sourceX: number,
    sourceY: number,
    sourceWidth: number,
    sourceHeight: number,
    outputWidth: number,
    outputHeight: number
  ): Promise<string> {
    // Create canvas with output dimensions
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d')!;

    // Draw cropped portion from source image, scaled to fit output canvas
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    // Convert to data URL
    return canvas.toDataURL('image/png');
  }

  private async captureHtmlToCanvas(
    options: CaptureOptions = {}
  ): Promise<string> {
    try {
      this.isCapturing = true;
      const {
        elementPath,
        fullPage = false,
        borderStyle = { color: 'red', width: 3, style: 'dashed' },
      } = options;

      // Save scroll positions before capture
      document.querySelectorAll('*').forEach((element) => {
        if (element.scrollTop > 0 || element.scrollLeft > 0) {
          this.saveElementState(element);
        }
      });

      // Configure dimensions based on full page or viewport mode
      let canvasConfig;

      if (fullPage) {
        // Full page configuration
        const documentWidth = Math.max(
          document.documentElement.scrollWidth,
          document.documentElement.offsetWidth,
          document.documentElement.clientWidth
        );
        const documentHeight = Math.max(
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
          document.documentElement.clientHeight
        );

        canvasConfig = {
          width: documentWidth,
          height: documentHeight,
          scale: 1, // Use scale 1 for full page to manage performance and file size
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0,
          windowWidth: documentWidth,
          windowHeight: documentHeight,
          useCORS: true,
          allowTaint: false,
        };
      } else {
        // Viewport configuration
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        canvasConfig = {
          width: viewportWidth,
          height: viewportHeight,
          scale: window.devicePixelRatio,
          x: scrollX,
          y: scrollY,
          scrollX: 0,
          scrollY: 0,
          windowWidth: viewportWidth,
          windowHeight: viewportHeight,
          useCORS: true,
          allowTaint: false,
        };
      }

      let targetElement: HTMLElement | null = null;
      let defaultStyle = 'none';
      if (elementPath) {
        targetElement = await getNearestNode(elementPath);
        defaultStyle = targetElement?.style.border || 'none';
        if (targetElement) {
          targetElement.style.border = `${borderStyle.width}px ${borderStyle.style} ${borderStyle.color} !important`;
        }
      }

      const canvas = await html2canvas(document.documentElement, {
        logging: false,
        backgroundColor: '#fff',
        ignoreElements: (element) => {
          if (element.id === 'root-atarim') return true;
          if (element.classList?.contains('no-screenshot')) return true;
          if (element.hasAttribute('data-html2canvas-ignore')) return true;
          return false;
        },
        onclone: (_clonedDoc: Document, clonedElement: HTMLElement) => {
          // Apply styles based on capture mode
          if (fullPage) {
            clonedElement.style.width = canvasConfig.width + 'px';
            clonedElement.style.height = canvasConfig.height + 'px';
            clonedElement.style.position = 'absolute';
            clonedElement.style.top = '0';
            clonedElement.style.left = '0';
            clonedElement.style.overflow = 'visible';
          } else {
            clonedElement.style.width =
              document.documentElement.scrollWidth + 'px';
            clonedElement.style.height =
              document.documentElement.scrollHeight + 'px';
            clonedElement.style.position = 'absolute';
            clonedElement.style.top = '0';
            clonedElement.style.left = '0';
          }
        },
        ...canvasConfig,
      });

      if (targetElement) {
        targetElement.style.border = defaultStyle;
      }

      return canvas.toDataURL('image/jpeg', options.quality || 0.95);
    } finally {
      this.isCapturing = false;
      this.restoreElementStates();
    }
  }

  private async captureChromeTabScreenshot(
    options: CaptureOptions
  ): Promise<string> {
    try {
      // Enable screenshot mode for consistency (flag used elsewhere in app)
      this.enableScreenshotMode();
      this.isCapturing = true;

      console.log('Using Chrome API with background script UI control');

      const quality = Math.round((options.quality || 0.95) * 100);
      const format = 'jpeg'; // Default to jpeg for better compression

      const borderStyle = options.borderStyle || {
        color: 'red',
        width: 3,
        style: 'dashed',
      };

      const dataUrl: string | { error: string } =
        await chrome.runtime.sendMessage({
          type: 'CAPTURE_VISIBLE_TAB',
          payload: {
            quality,
            format,
            fullPage: options.fullPage || false,
            elementSelector: options.elementPath, // Use existing elementPath
            borderStyle,
          },
        });

      // Check if background script returned an error object
      if (typeof dataUrl === 'object' && dataUrl.error) {
        throw new Error(`Background script error: ${dataUrl.error}`);
      }
      // Basic validation of the data URL
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image')) {
        throw new Error(
          'Invalid screenshot data received from background script'
        );
      }

      return dataUrl;
    } catch (error) {
      console.error('Chrome API screenshot capture failed:', error);
      throw error; // Rethrow to allow fallback in main capture method
    } finally {
      // Restore screenshot mode and states
      this.disableScreenshotMode();
      this.restoreElementStates();
      this.isCapturing = false;
    }
  }

  private async captureBrowserScreen(): Promise<string> {
    try {
      await this.hideUIElements();

      // Add additional delay to ensure UI changes are stable before browser capture
      await new Promise((resolve) =>
        setTimeout(resolve, isInIframe() ? 200 : 100)
      );

      const mediaOptions = {
        video: {
          cursor: 'never',
          displaySurface: 'browser',
        },
        audio: false,
        preferCurrentTab: true,
        monitorTypeSurfaces: 'exclude',
        selfBrowserSurface: 'include',
      };

      const stream = await navigator.mediaDevices.getDisplayMedia(mediaOptions);
      const video = document.createElement('video');
      video.autoplay = true;
      video.srcObject = stream;

      // Wait for video metadata AND ensure it's stable
      await new Promise((resolve) => {
        video.addEventListener('loadedmetadata', () => {
          // Add an additional delay after metadata loads
          if (isInIframe()) {
            setTimeout(resolve, 1000);
          } else {
            setTimeout(resolve, 200);
          }
        });
      });

      // Create the canvas for capture
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      // Get dimensions
      const trackSettings = stream.getVideoTracks()[0].getSettings();
      const documentElement = document.documentElement;

      canvas.width =
        trackSettings.width ||
        documentElement.scrollWidth ||
        documentElement.clientWidth ||
        window.innerWidth;

      canvas.height =
        trackSettings.height ||
        documentElement.scrollHeight ||
        documentElement.clientHeight ||
        window.innerHeight;

      // Ensure minimum dimensions
      canvas.width = Math.max(canvas.width, 100);
      canvas.height = Math.max(canvas.height, 100);

      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
      }

      stream.getTracks().forEach((track) => track.stop());

      this.restoreElementStates();
      return canvas.toDataURL('image/jpeg', 1.0);
    } catch (error) {
      this.restoreElementStates();
      console.error('Browser screen capture failed:', error);
      throw error;
    }
  }

  private saveElementState(element: Element) {
    const htmlElement = element as HTMLElement;
    const state: {
      opacity: string;
      pointerEvents: string;
      scrollTop: number;
      scrollLeft: number;
      value?: string;
      checked?: boolean;
      outline?: string;
      transition?: string;
      visibility?: string;
      display?: string; // Added display tracking
    } = {
      opacity: htmlElement.style.opacity,
      pointerEvents: htmlElement.style.pointerEvents,
      scrollTop: element.scrollTop,
      scrollLeft: element.scrollLeft,
      outline: htmlElement.style.outline,
      transition: htmlElement.style.transition,
      visibility: htmlElement.style.visibility,
      display: htmlElement.style.display, // Save display state
    };

    if (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement
    ) {
      state.value = element.value;
      if (element.type === 'checkbox' || element.type === 'radio') {
        if (element instanceof HTMLInputElement) {
          state.checked = element.checked;
        }
      }
    }

    this.originalStates.set(element, state);
    return state;
  }

  public restoreElementStates() {
    try {
      // Restore body overflow
      document.body.style.overflow = this._originalBodyOverflow;

      // Restore all saved element states
      this.originalStates.forEach((state, element) => {
        try {
          const htmlElement = element as HTMLElement;

          if (htmlElement && htmlElement.style) {
            // Restore display property first
            if (state.display !== undefined) {
              htmlElement.style.display = state.display;
            } else {
              htmlElement.style.removeProperty('display');
            }

            // Restore other properties
            if (state.opacity !== undefined) {
              htmlElement.style.opacity = state.opacity;
            } else {
              htmlElement.style.removeProperty('opacity');
            }

            if (state.pointerEvents !== undefined) {
              htmlElement.style.pointerEvents = state.pointerEvents;
            } else {
              htmlElement.style.removeProperty('pointerEvents');
            }

            if (state.visibility !== undefined) {
              htmlElement.style.visibility = state.visibility;
            } else {
              htmlElement.style.removeProperty('visibility');
            }

            if (state.outline !== undefined) {
              htmlElement.style.outline = state.outline;
            } else {
              htmlElement.style.removeProperty('outline');
            }

            if (state.transition !== undefined) {
              htmlElement.style.transition = state.transition;
            } else {
              htmlElement.style.removeProperty('transition');
            }

            if (state.scrollTop !== undefined) {
              element.scrollTop = state.scrollTop;
            }

            if (state.scrollLeft !== undefined) {
              element.scrollLeft = state.scrollLeft;
            }

            if (
              state.value !== undefined &&
              (element instanceof HTMLInputElement ||
                element instanceof HTMLTextAreaElement ||
                element instanceof HTMLSelectElement)
            ) {
              element.value = state.value;
            }

            if (
              state.checked !== undefined &&
              element instanceof HTMLInputElement
            ) {
              element.checked = state.checked;
            }
          }
        } catch (e) {
          console.warn('Error restoring element state:', e);
        }
      });

      this.originalStates.clear();
      this._uiHidden = false; // Mark UI as restored
    } catch (error) {
      console.error('Error in restoreElementStates:', error);
    }
  }

  public async hideUIElements(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Find the root Atarim element
        const rootAtarim = document.getElementById('root-atarim');

        if (rootAtarim) {
          // Save the original display value
          this.saveElementState(rootAtarim);
          // Hide the entire Atarim UI
          rootAtarim.style.display = 'none';
        }

        // If there's a selected element, apply highlight with no transition
        if (this.selectedElementRef) {
          // Do not overwrite previously saved state if it already exists
          if (!this.originalStates.has(this.selectedElementRef)) {
            this.saveElementState(this.selectedElementRef);
          }
          this.selectedElementRef.style.transition = 'none';
          // Only set a default outline if one hasn't already been applied by the caller
          if (!this.selectedElementRef.style.outline) {
            this.selectedElementRef.style.setProperty(
              'outline',
              '3px dashed red',
              'important'
            );
          }
        }

        // Use multiple requestAnimationFrame calls to ensure DOM changes are fully processed
        // This ensures the browser has completed layout, paint, and composite phases
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Add a small delay to ensure changes are stable, especially for background script timing
            setTimeout(() => {
              this._uiHidden = true; // Mark UI as hidden
              resolve();
            }, 50); // Increased delay for more reliable timing
          });
        });
      } catch (error) {
        console.error('Error in hideUIElements:', error);
        reject(error);
      }
    });
  }

  /**
   * Ensures UI restoration waits for any ongoing async operations
   * Useful when screenshot operations involve background scripts
   */
  public async ensureUIRestoration(minDelay: number = 100): Promise<void> {
    return new Promise((resolve) => {
      // Wait for minimum delay to ensure screenshot operations are complete
      setTimeout(() => {
        if (this._uiHidden) {
          this.restoreElementStates();
        }
        resolve();
      }, minDelay);
    });
  }

  /**
   * Check if UI is currently hidden
   */
  public isUIHidden(): boolean {
    return this._uiHidden;
  }

  private isElementVisible(element: Element): boolean {
    const style = window.getComputedStyle(element);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0'
    );
  }

  public async capture(
    options: CaptureOptions = {}
  ): Promise<string | undefined> {
    const isExtensionContext =
      typeof chrome !== 'undefined' && chrome.runtime?.id;

    try {
      // Dispatch action or call callback to notify capture is starting
      if (options.onCaptureStart) {
        options.onCaptureStart();
      }

      // Wait a moment for the style changes to take effect
      await new Promise<void>((resolve) => setTimeout(resolve, 200));

      // Chrome API now supports both viewport and full page with automatic UI hiding
      if (isExtensionContext && options.useChromeApi) {
        console.log(
          options.fullPage
            ? 'using chrome api for full page with UI control'
            : 'using chrome api for viewport with UI control'
        );
        return await this.captureChromeTabScreenshot(options);
      }

      if (options.useSnapdom) {
        console.log('using snapdom');
        return await this.captureSnapdom(options);
      }

      // For non-Chrome contexts or when Chrome API is disabled
      if (options.fullPage) {
        console.log('using html-to-image for full page capture');
        return await this.captureHtmlToImage(options);
      }

      // For viewport captures, use the original priority order
      if (options.useBrowser) {
        console.log('using browser capture');
        return await this.captureBrowserScreen();
      }
      if (options.useHtml2Canvas) {
        console.log('using html2canvas');
        return await this.captureHtmlToCanvas(options);
      }
      console.log('using html-to-image');
      return await this.captureHtmlToImage(options);
    } catch (error) {
      console.warn(
        'Primary capture method failed, falling back to browser capture:',
        error
      );
      if (options.useFallback) {
        return await this.captureBrowserScreen();
      }
    } finally {
      // Dispatch action or call callback to notify capture is ending
      if (options.onCaptureEnd) {
        options.onCaptureEnd();
      }
    }
  }

  public setSelectedElement(element: HTMLElement | null) {
    // Store reference
    this.selectedElementRef = element;

    // Clear any existing outline first
    if (this.selectedElementRef && !this.isCapturing) {
      // Save the current state if we haven't already
      if (!this.originalStates.has(this.selectedElementRef)) {
        this.saveElementState(this.selectedElementRef);
      }
      this.selectedElementRef.style.setProperty(
        'outline',
        '3px dashed red',
        'important'
      );
    }
  }

  public clearSelectedElement() {
    if (this.selectedElementRef && !this.isCapturing) {
      this.selectedElementRef.style.removeProperty('outline');
      this.selectedElementRef = null;
    }
  }

  // Cleanup method - simplified since we removed worker
  public destroy() {
    this.originalStates.clear();
  }
}
